import { useEffect, useRef } from "react";
import { SparkleIcon, ChartBarIcon, WarningIcon } from "@phosphor-icons/react";
import { isEnterSubmit } from "@libs/zapi-ui/custom";

type TProps = {
  value: string;
  onChange: (value: string) => void;
  onCancel: () => void;
  onCreate: () => void;
  error: string | null;
};

export function SelectionImagePrompt({ value, onChange, onCancel, onCreate, error }: TProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const canCreate = value.trim().length > 0;

  return (
    <div className="bg-white border border-innoveraZinc-150 rounded-lg shadow-lg overflow-hidden w-[420px]">
      <div className="bg-zinc-50 flex flex-col gap-3 p-3">
        <div className="flex items-center gap-2 py-1">
          <ChartBarIcon className="h-4 w-4 text-zinc-900 shrink-0" />
          <span className="text-[14px] font-medium leading-[13px] text-zinc-900">Chart</span>
          <span className="bg-violet-100 text-[#7c3aed] text-[12px] font-medium leading-none px-1 py-1 rounded">AI</span>
        </div>
        <textarea
          ref={textareaRef}
          data-testid="chart-prompt-textarea"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && isEnterSubmit(e, { allowShift: true }) && canCreate) {
              e.preventDefault();
              onCreate();
            } else if (e.key === "Escape") {
              e.preventDefault();
              onCancel();
            }
          }}
          placeholder="e.g. Show pain points as a ranked bar chart..."
          className="block w-full h-24 px-3 py-2 text-[14px] leading-[22px] bg-white border border-innoveraZinc-150 rounded-lg resize-none focus:outline-hidden focus:border-zinc-400 placeholder:text-zinc-400"
        />
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            data-testid="chart-prompt-cancel-button"
            onClick={onCancel}
            className="h-8 px-3 rounded-lg border border-innoveraZinc-150 bg-white text-[14px] font-medium text-zinc-900 hover:bg-zinc-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="chart-prompt-create-button"
            onClick={onCreate}
            disabled={!canCreate}
            className={
              canCreate
                ? "h-8 px-3 rounded-lg flex items-center gap-1 text-[14px] font-medium bg-zinc-900 text-white hover:bg-zinc-800"
                : "h-8 px-3 rounded-lg flex items-center gap-1 text-[14px] font-medium bg-zinc-100 text-zinc-400 cursor-not-allowed"
            }
          >
            <SparkleIcon className="h-4 w-4" />
            Create
          </button>
        </div>
      </div>
      {error && (
        <div className="bg-red-50 border-t border-innoveraZinc-150 flex items-start gap-2 px-3 py-2.5">
          <WarningIcon className="h-4 w-4 text-[#D93D3D] mt-[2px] shrink-0" />
          <span className="text-[14px] leading-[22px] text-[#D93D3D]">{error}</span>
        </div>
      )}
    </div>
  );
}
